import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Bell, Trash2, Plus } from 'lucide-react';
import API from '../services/api';

const Alertes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    marque: '', modele: '', prix_min: '', prix_max: '',
    carburant: '', transmission: '', type_vehicule: ''
  });

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    chargerAlertes();
  }, [user]);

  const chargerAlertes = async () => {
    try {
      const res = await API.get('/alertes');
      setAlertes(res.data.alertes);
    } finally {
      setLoading(false);
    }
  };

  const handleCreer = async (e) => {
    e.preventDefault();
    try {
      await API.post('/alertes', form);
      setMessage('✅ Alerte créée avec succès !');
      setShowForm(false);
      setForm({ marque: '', modele: '', prix_min: '', prix_max: '', carburant: '', transmission: '' });
      chargerAlertes();
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const handleSupprimer = async (id) => {
    if (!window.confirm('Supprimer cette alerte ?')) return;
    await API.delete(`/alertes/${id}`);
    setAlertes(alertes.filter(a => a.id !== id));
  };

  const handleToggle = async (id) => {
    const res = await API.put(`/alertes/${id}/toggle`);
    setAlertes(alertes.map(a => a.id === id ? res.data.alerte : a));
  };

  const inputClass = "w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400 transition";
  const labelClass = "block text-xs font-semibold text-gray-500 mb-1";

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div style={{ background: '#0A0A0A' }} className="py-8 px-4">
        <div className="max-w-3xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-white text-2xl font-black flex items-center gap-2">
              <Bell size={24} style={{ color: '#F5A623' }} />
              Mes Alertes
            </h1>
            <p style={{ color: '#F5A623' }} className="text-sm mt-1">
              Soyez notifié quand une annonce correspond à vos critères
            </p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            style={{ background: '#F5A623' }}
            className="text-black px-4 py-2 rounded-xl font-bold flex items-center gap-2 hover:opacity-90 transition"
          >
            <Plus size={16} /> Nouvelle alerte
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">

        {message && (
          <div className={`p-4 rounded-2xl mb-6 text-sm font-semibold ${
            message.startsWith('✅')
              ? 'bg-green-50 border border-green-200 text-green-600'
              : 'bg-red-50 border border-red-200 text-red-600'
          }`}>
            {message}
          </div>
        )}

        {/* Formulaire nouvelle alerte */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <h2 className="font-black text-gray-900 mb-4 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Créer une alerte
            </h2>
            <form onSubmit={handleCreer} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Marque</label>
                  <input type="text" value={form.marque}
                    onChange={(e) => setForm({ ...form, marque: e.target.value })}
                    className={inputClass} placeholder="Toyota, Peugeot..."
                  />
                </div>
                <div>
                  <label className={labelClass}>Modèle</label>
                  <input type="text" value={form.modele}
                    onChange={(e) => setForm({ ...form, modele: e.target.value })}
                    className={inputClass} placeholder="Prado, 308..."
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Prix min (FCFA)</label>
                  <input type="number" value={form.prix_min}
                    onChange={(e) => setForm({ ...form, prix_min: e.target.value })}
                    className={inputClass} placeholder="0"
                  />
                </div>
                <div>
                  <label className={labelClass}>Prix max (FCFA)</label>
                  <input type="number" value={form.prix_max}
                    onChange={(e) => setForm({ ...form, prix_max: e.target.value })}
                    className={inputClass} placeholder="10000000"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Carburant</label>
                  <select value={form.carburant}
                    onChange={(e) => setForm({ ...form, carburant: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Tous</option>
                    <option value="essence">Essence</option>
                    <option value="diesel">Diesel</option>
                    <option value="electrique">Électrique</option>
                    <option value="hybride">Hybride</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Transmission</label>
                  <select value={form.transmission}
                    onChange={(e) => setForm({ ...form, transmission: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Toutes</option>
                    <option value="manuelle">Manuelle</option>
                    <option value="automatique">Automatique</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Type de véhicule</label>
                  <select
                    value={form.type_vehicule || ''}
                    onChange={(e) => setForm({ ...form, type_vehicule: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                  >
                    <option value="">Tous les types</option>
                    <option value="berline">Berline</option>
                    <option value="suv">SUV</option>
                    <option value="4x4">4x4</option>
                    <option value="pickup">Pick-up</option>
                    <option value="utilitaire">Utilitaire</option>
                    <option value="engin">Engin</option>
                    <option value="citadine">Citadine</option>
                    <option value="monospace">Monospace</option>
                    <option value="moto">Moto</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 bg-gray-100 text-gray-600 py-2 rounded-xl font-bold hover:bg-gray-200 transition"
                >
                  Annuler
                </button>
                <button type="submit"
                  style={{ background: '#F5A623' }}
                  className="flex-1 text-black py-2 rounded-xl font-bold hover:opacity-90 transition"
                >
                  Créer l'alerte
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Liste des alertes */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : alertes.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <Bell size={60} className="mx-auto mb-4 text-gray-200" />
            <p className="text-gray-400 font-semibold mb-2">Aucune alerte configurée</p>
            <p className="text-gray-400 text-sm mb-6">
              Créez une alerte pour être notifié quand une annonce correspond à vos critères
            </p>
            <button
              onClick={() => setShowForm(true)}
              style={{ background: '#F5A623' }}
              className="text-black px-6 py-2 rounded-xl font-bold hover:opacity-90 transition"
            >
              Créer ma première alerte
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {alertes.map(alerte => (
              <div key={alerte.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Bell size={16} style={{ color: '#F5A623' }} />
                      <h3 className="font-black text-gray-900">
                        {alerte.marque || 'Toutes marques'} {alerte.modele || ''}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        alerte.actif ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {alerte.actif ? '🔔 Active' : '🔕 Inactive'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                      {alerte.prix_min && (
                        <span className="bg-gray-100 px-2 py-1 rounded-lg">
                          Min: {parseInt(alerte.prix_min).toLocaleString('fr-FR')} FCFA
                        </span>
                      )}
                      {alerte.prix_max && (
                        <span className="bg-gray-100 px-2 py-1 rounded-lg">
                          Max: {parseInt(alerte.prix_max).toLocaleString('fr-FR')} FCFA
                        </span>
                      )}
                      {alerte.carburant && (
                        <span className="bg-gray-100 px-2 py-1 rounded-lg capitalize">
                          {alerte.carburant}
                        </span>
                      )}
                      {alerte.transmission && (
                        <span className="bg-gray-100 px-2 py-1 rounded-lg capitalize">
                          {alerte.transmission}
                        </span>
                      )}
                    </div>
                    {alerte.derniere_notification && (
                      <p className="text-xs text-gray-400 mt-2">
                        Dernière notification : {new Date(alerte.derniere_notification).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleToggle(alerte.id)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                        alerte.actif
                          ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          : 'bg-green-100 text-green-600 hover:bg-green-200'
                      }`}
                    >
                      {alerte.actif ? 'Désactiver' : 'Activer'}
                    </button>
                    <button
                      onClick={() => handleSupprimer(alerte.id)}
                      className="p-2 text-red-400 hover:bg-red-50 rounded-xl transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Alertes;